<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue'
import {IonButton, IonItem} from '@ionic/vue'
import {BookingSuggestion, Jobsite} from '@/graphql/generated/graphql'
import {fetchBookingSuggestions, fetchBookingSuggestionsByEmployeeIds, setBookingSuggestionToSeen} from '@/helper/bookingSuggestion'
import RichResponseListItem from '@/views/Chat/RichResponses/components/RichResponseListItem.vue'
import {kapitelDateString, kapitelDateTimeString} from '@/graphql/kapitelTypes'
import datetime from '@/helper/datetime/datetime'
import datetimeRange from '@/helper/datetime/datetimeRange'
import JobsiteDemandDayDetail from '@/views/JobsiteDemandDayDetail.vue'
import RichResponseListItemGroup from "@/views/Chat/RichResponses/components/RichResponseListItemGroup.vue";
import {DataLoader} from '@/helper/dataLoader'
import date from '@/helper/datetime/date'
import RichResponseHeadline from "@/views/Chat/RichResponses/components/RichResponseHeadline.vue";
import {mailOutline} from "ionicons/icons";
import Skeleton from "@/components/Skeleton.vue";
import {useGlobalEmitter} from "@/helper/emitter";
import {isElementVisibleInScrollContainer} from "@/helper/dom";

const props = defineProps<{
    employeeIds?: number[]
    active?: boolean
}>()

const suggestions = ref<BookingSuggestion[]>([])
const detailOpen = ref(false)
const detailDate = ref<kapitelDateString | undefined>(undefined)
const detailJobsite = ref<Jobsite | undefined>(undefined)
const detailWithdrawer = ref<number|undefined>(undefined)
const listContainer = ref<HTMLElement | null>(null)
const scrollElement = ref<HTMLElement | null>(null)
const scrollEventTarget = ref<HTMLElement | Window | null>(null)
const scrollListener = ref<((event?: Event) => void) | null>(null)
const pendingRequestIds = new Set<number>()
const markedRequestIds = new Set<number>()
const showNonMatching = ref(false)

const isActive = computed(() => props.active !== false)
const effectiveEmployeeIds = computed(() => props.employeeIds)

const loader = new DataLoader([
    async () => {
        if (!isActive.value) return
        if (effectiveEmployeeIds.value !== undefined) {
            suggestions.value = await fetchBookingSuggestionsByEmployeeIds(effectiveEmployeeIds.value)
            return
        }
        suggestions.value = await fetchBookingSuggestions(true, showNonMatching.value)
    }
])

const filteredGroupedSuggestions = computed(() => suggestions.value)

useGlobalEmitter().on('day:mutated', () => {
    if (isActive.value) {
        loader.reload()
    }
});

onMounted(() => {
    if (isActive.value) {
        loader.load()
    }
})

watch(() => props.active, (active) => {
    if (active) {
        loader.load()
    }
})

watch(() => props.employeeIds, () => {
    if (isActive.value) {
        loader.reload()
    }
})

watch(detailOpen, (open) => {
    if (!open) {
        detailDate.value = undefined
        detailJobsite.value = undefined
        detailWithdrawer.value = undefined
    }
})

type MonthGroup = { monthKey: string, label: string, items: BookingSuggestion[] }
type MessageGroup = { message: string, months: MonthGroup[] }
type WithdrawerGroup = { withdrawerName: string, messages: MessageGroup[] }

const groupedSuggestions = computed<WithdrawerGroup[]>(() => {
    const byWithdrawer: Record<string, { messages: Record<string, Record<string, BookingSuggestion[]>>, latestCreated?: kapitelDateTimeString }> = {}

    const cmpSuggestion = (a: BookingSuggestion, b: BookingSuggestion) => date.cmpKapitelDate(a.request.booking.date, b.request.booking.date)

    const monthLabelFromDate = (bookingDate?: kapitelDateString) => {
        if (!bookingDate) return 'Unbekannter Monat'
        return `${date.formatMonthFromKapitelDate(bookingDate)} ${date.getYearFromKapitelDate(bookingDate)}`
    }

    for (const suggestion of filteredGroupedSuggestions.value) {
        if (!suggestion.request || !suggestion.request.booking) {
            continue
        }
        const withdrawer = suggestion.withdrawerName || 'Unbekannt'
        const message = suggestion.request?.message || 'Ohne Nachricht'
        const monthKey = date.startOfMonth(suggestion.request.booking.date)

        byWithdrawer[withdrawer] ??= {messages: {}, latestCreated: undefined}
        byWithdrawer[withdrawer].messages[message] ??= {}
        byWithdrawer[withdrawer].messages[message][monthKey] ??= []

        byWithdrawer[withdrawer].messages[message][monthKey].push(suggestion)

        const created = suggestion.request?.created
        const latestCreated = byWithdrawer[withdrawer].latestCreated
        if (created && (!latestCreated || datetime.isAfter(created, latestCreated))) {
            byWithdrawer[withdrawer].latestCreated = created
        }
    }

    return Object.entries(byWithdrawer)
        .map(([withdrawerName, {messages, latestCreated}]) => ({
            withdrawerName,
            latestCreated,
            messages: Object.entries(messages).map(([message, months]) => {
                const monthGroups: MonthGroup[] = Object.entries(months)
                    .map(([monthKey, items]) => {
                        const sortedItems = [...items].sort(cmpSuggestion)
                        const bookingDate = sortedItems.find(s => s.request?.booking?.date)?.request?.booking?.date
                        return {
                            monthKey,
                            label: monthLabelFromDate(bookingDate as kapitelDateString | undefined),
                            items: sortedItems
                        }
                    })
                    .sort((a, b) => a.monthKey.localeCompare(b.monthKey))

                return {message, months: monthGroups}
            })
        }))
        .sort((a, b) => -datetime.cmpKapitelDateTime(a.latestCreated, b.latestCreated))
})

const onSuggestionClick = (suggestion: BookingSuggestion) => {
    const booking = suggestion.request?.booking
    if (!booking) return
    detailDate.value = booking.date
    detailJobsite.value = booking.jobsite as Jobsite
    detailOpen.value = true
    detailWithdrawer.value = suggestion.withdrawerId
}

const toggleShowNonMatching = () => {
    showNonMatching.value = !showNonMatching.value
    loader.reload()
}

const suggestionKey = (s: BookingSuggestion) => {
    const booking = s.request?.booking
    if (!booking) return `${Math.random()}`
    return `${booking.jobsite.id}-${booking.begin}-${booking.until}-${s.withdrawerId ?? 'none'}`
}

const markSuggestionSeen = async (suggestion: BookingSuggestion) => {
    const requestId = suggestion.request?.id
    if (!requestId || suggestion.seen || pendingRequestIds.has(requestId) || markedRequestIds.has(requestId)) return
    pendingRequestIds.add(requestId)
    try {
        await setBookingSuggestionToSeen(requestId)
        markedRequestIds.add(requestId)
    } finally {
        pendingRequestIds.delete(requestId)
    }
}

const checkVisibleSuggestions = () => {
    if (!listContainer.value || !scrollElement.value) return
    const items = listContainer.value.querySelectorAll<HTMLElement>('[data-request-id]')
    items.forEach((item) => {
        const requestId = Number(item.dataset.requestId)
        if (!requestId) return
        const suggestion = suggestions.value.find(s => s.request?.id === requestId)
        if (!suggestion || suggestion.seen) return
        if (isElementVisibleInScrollContainer(item, scrollElement.value as HTMLElement)) {
            void markSuggestionSeen(suggestion)
        }
    })
}

const scheduleVisibilityCheck = () => {
    requestAnimationFrame(() => checkVisibleSuggestions())
}

const resolveScrollElement = async () => {
    if (!listContainer.value) return
    const modalBody = listContainer.value.closest('.body') as HTMLElement | null
    if (modalBody) {
        scrollElement.value = modalBody
        scrollEventTarget.value = modalBody
        return
    }
    const ionContent = listContainer.value.closest('ion-content') as HTMLElement & { getScrollElement?: () => Promise<HTMLElement> }
    if (ionContent?.getScrollElement) {
        scrollElement.value = await ionContent.getScrollElement()
        scrollEventTarget.value = scrollElement.value
        return
    }
    scrollElement.value = document.documentElement
    scrollEventTarget.value = window
}

onMounted(async () => {
    await resolveScrollElement()
    scrollListener.value = () => checkVisibleSuggestions()
    const target = scrollEventTarget.value
    if (target && scrollListener.value) {
        target.addEventListener('scroll', scrollListener.value, {passive: true} as AddEventListenerOptions)
    }
    scheduleVisibilityCheck()
})

onBeforeUnmount(() => {
    const target = scrollEventTarget.value
    if (target && scrollListener.value) {
        target.removeEventListener('scroll', scrollListener.value as EventListener)
    }
    scrollListener.value = null
    scrollElement.value = null
    scrollEventTarget.value = null
})

watch(suggestions, () => {
    scheduleVisibilityCheck()
})

watch(() => loader.pending.value, (pending) => {
    if (!pending) {
        scheduleVisibilityCheck()
    }
})
</script>

<template>
    <div ref="listContainer">
        <Skeleton v-if="loader.pending.value" :items="['block--height-5em', 'block--height-5em']" />
        <template v-else>
            <div v-if="groupedSuggestions.length === 0" class="text-medium">Keine Vorschläge vorhanden.</div>
            <template v-for="withdrawerGroup in groupedSuggestions" :key="withdrawerGroup.withdrawerName">
                <RichResponseHeadline>{{ withdrawerGroup.withdrawerName }}</RichResponseHeadline>
                <RichResponseListItemGroup v-for="messageGroup in withdrawerGroup.messages" :key="messageGroup.message" class="message-block">
                    <RichResponseListItem
                        :icon="mailOutline"
                        icon-type="ion"
                    >
                        {{ messageGroup.message }}
                    </RichResponseListItem>
                    <template v-for="monthGroup in messageGroup.months" :key="monthGroup.monthKey">
                        <div class="divider">
                            <ion-item>
                                {{monthGroup.label}}
                            </ion-item>
                        </div>
                        <template
                            v-for="suggestion in monthGroup.items"
                            :key="suggestionKey(suggestion)"
                        >
                            <RichResponseListItem
                                :title="suggestion.request?.booking?.jobsite?.name || 'Dienst'"
                                :text="datetimeRange.formatTimeRange(suggestion.request?.booking.begin, suggestion.request?.booking.until)"
                                :icon="suggestion.request?.booking?.date"
                                icon-type="date"
                                :button="true"
                                :badge="!suggestion.seen"
                                :badge-value="''"
                                @click="() => onSuggestionClick(suggestion)"
                            />
                        </template>
                    </template>
                </RichResponseListItemGroup>
            </template>
            <IonButton
                v-if="effectiveEmployeeIds === undefined"
                class="toggle-non-matching"
                expand="block"
                color="light"
                @click="toggleShowNonMatching"
            >
                {{ showNonMatching ? 'Zeige passende Dienstvorschläge' : 'Zeige alle offenen Dienstvorschläge' }}
            </IonButton>
        </template>
    </div>
    <JobsiteDemandDayDetail
        v-if="detailDate && detailJobsite"
        v-model="detailOpen"
        :date="detailDate"
        :jobsite="detailJobsite"
        :withdrawal-request-original-employee-id="detailWithdrawer"
        :showJobsiteDetailModalLink="true"
    />
</template>

<style scoped lang="scss">
.message-block {
    margin-bottom: 1em;
}
.divider{
    ion-item {
        height:2rem;
        &::part(native) {
            display: inline-block;
            font-size: var(--custom-font-size-small);
            font-weight: var(--custom-font-weight-extrabold);
            background-color: var(--ion-color-light);
        }
    }
}
.toggle-non-matching {
    margin-top: 1rem;
}
</style>
